import { useCallback, useMemo } from 'react';

import { Box, Flex } from '@bonusly/design-system';

import { BizySettingsModal } from '../../modules/bizy-settings/bizy-settings';
import { BizySidePanelChatView } from './bizy-side-panel-chat-view';
import { BizySidePanelMenu } from './bizy-side-panel-menu';
import { BizySidePanelResizeHandle } from './bizy-side-panel-resize-handle';
import { BizyPeopleBrowser } from './components/bizy-people-browser';
import { BizyPersonView } from './components/bizy-person-view';
import { useBizyOpenListener } from './hooks/use-bizy-open-listener';
import { usePeopleBrowserEnabled } from './hooks/use-people-browser-enabled';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';

export type BizyPanelGradientTheme = 'none' | 'cool-subtle';

/**
 * Renders the settings modal while subscribing to its open-state HERE rather
 * than in BizySidePanel. Keeping the isSettingsModalOpen/settingsSection reads
 * out of the parent means toggling settings re-renders only this host — not the
 * whole panel (and its ~1,900-line chat view). Mirrors PromptLibraryModalHost.
 */
function BizySettingsModalHost() {
	const isSettingsModalOpen = useBizySidePanelStore((state) => state.isSettingsModalOpen);
	const settingsSection = useBizySidePanelStore((state) => state.settingsSection);
	const closeSettingsModal = useBizySidePanelStore((state) => state.closeSettingsModal);
	const setSettingsSection = useBizySidePanelStore((state) => state.setSettingsSection);

	return (
		<BizySettingsModal
			isOpen={isSettingsModalOpen}
			onClose={closeSettingsModal}
			section={settingsSection}
			onSelectSection={setSettingsSection}
			showBizyOptOut
		/>
	);
}

interface BizySidePanelProps {
	gradientTheme?: BizyPanelGradientTheme;
	isMobileMode?: boolean;
	panelLeftEdge: number;
	showFullscreenToggle?: boolean;
}

export function BizySidePanel({ gradientTheme = 'none', isMobileMode = false, panelLeftEdge, showFullscreenToggle = true }: BizySidePanelProps) {
	const isOpen = useBizySidePanelStore((state) => state.isOpen);
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);
	const view = useBizySidePanelStore((state) => state.view);
	const showHistory = useBizySidePanelStore((state) => state.showHistory);
	const isHistoryDrawerOpen = useBizySidePanelStore((state) => state.isHistoryDrawerOpen);
	const viewedPerson = useBizySidePanelStore((state) => state.viewedPerson);
	const isPeopleBrowserCollapsed = useBizySidePanelStore((state) => state.isPeopleBrowserCollapsed);
	// Conversation id lives in the store (not local state) so URL sync can put it
	// in navigation state for `?bizy=chat-with-bizy` (BIZY-680). The chat view
	// still drives it via onConversationIdChange below.
	const setActiveConversationId = useBizySidePanelStore((state) => state.setActiveConversationId);

	const isPeopleBrowserEnabled = usePeopleBrowserEnabled();

	useBizyOpenListener();

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key === 'Escape' && !e.defaultPrevented) {
			const store = useBizySidePanelStore.getState();
			if (store.isSettingsModalOpen) {
				store.closeSettingsModal();
			} else if (store.isHistoryDrawerOpen) {
				store.closeHistoryDrawer();
			} else if (store.isFullscreen) {
				store.exitFullscreen();
			} else {
				store.close('keyboard');
			}
		}
	}, []);

	// Opaque base (via bgColor below) with a near-transparent grey wash on top,
	// so no page theme ever shows through the panel (BIZY-669). Uses raw palette
	// tokens (white / gray.900) because custom and seasonal themes repaint the
	// semantic bg.* variables.
	const PANEL_BG = 'linear-gradient(171deg, rgba(107, 116, 133, 0.00) 22.07%, rgba(107, 116, 133, 0.04) 100%)';
	const compactSlideTransition = 'transform 0.25s ease-in-out, opacity 0.25s ease-in-out';

	const historyBg = useMemo(() => {
		if (gradientTheme === 'none') return PANEL_BG;
		return 'linear-gradient(180deg, var(--chakra-colors-bg-canvas) 0%, var(--chakra-colors-bg-surface) 100%)';
	}, [gradientTheme]);

	const panelBg = view === 'history' ? historyBg : PANEL_BG;
	const isFullscreenLayout = isFullscreen && !isMobileMode;
	const canShowFullscreenToggle = showFullscreenToggle && !isMobileMode;

	return (
		<Flex
			data-component="bizy-side-panel"
			direction="column"
			h="100%"
			bgColor="white"
			bgImage={panelBg}
			_dark={{ bgColor: 'gray.900' }}
			borderLeft="1px solid"
			borderColor="border.soft"
			position="relative"
			overflow="hidden"
			overscrollBehavior="contain"
			onKeyDown={handleKeyDown}
			{...(!isOpen && { inert: '' })}
		>
			<Flex direction={isFullscreenLayout ? 'row' : 'column'} h="100%" minH={0} display={view === 'chat' ? 'flex' : 'none'}>
				{/* Fullscreen shows the People Browser as a persistent left column. In
				    compact mode it lives behind the "‹ Bizy" menu instead (see the
				    BizySidePanelMenu in the slide-in slot below) so the panel opens
				    straight into the chat. */}
				{isFullscreenLayout && isPeopleBrowserEnabled && !isPeopleBrowserCollapsed && (
					<Box flexShrink={0} h="100%" w="292px" borderRightWidth="1px" borderColor="border.soft" overflow="hidden">
						<BizyPeopleBrowser />
					</Box>
				)}
				{/* Chat view + person view live in ONE stable position across both
				    layouts (not a separate branch per layout), so fullscreen toggling
				    changes only this wrapper's layout props — never remounts the
				    ~1,900-line chat view. */}
				<Box
					position="relative"
					h="100%"
					minH={0}
					minW={0}
					flex={isFullscreenLayout ? 1 : undefined}
					w={isFullscreenLayout ? undefined : '100%'}
					overflow="hidden"
				>
					<Box
						position={!isFullscreenLayout && isHistoryDrawerOpen ? 'absolute' : 'relative'}
						top={0}
						left={0}
						right={0}
						h="100%"
						minH={0}
						transform={!isFullscreenLayout && isHistoryDrawerOpen ? 'translateX(100%)' : 'translateX(0)'}
						opacity={!isFullscreenLayout && isHistoryDrawerOpen ? 0 : 1}
						visibility={!isFullscreenLayout && isHistoryDrawerOpen ? 'hidden' : 'visible'}
						// Only animate the compact history-drawer slide, not fullscreen's
						// own (instant) entry/exit.
						transition={isFullscreenLayout ? 'none' : compactSlideTransition}
						pointerEvents={!isFullscreenLayout && isHistoryDrawerOpen ? 'none' : 'auto'}
						{...(!isFullscreenLayout && isHistoryDrawerOpen && { inert: '' })}
					>
						<BizySidePanelChatView
							onConversationIdChange={setActiveConversationId}
							onOpenHistory={showHistory}
							showFullscreenToggle={canShowFullscreenToggle}
						/>
						{/* Person page (stub) covers the chat while keeping it mounted (BIZY-636). */}
						{viewedPerson && (
							<Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={3} bg="white" _dark={{ bg: 'gray.900' }}>
								<BizyPersonView showFullscreenToggle={canShowFullscreenToggle} />
							</Box>
						)}
					</Box>
					{!isFullscreenLayout && isPeopleBrowserEnabled && (
						<Box
							position={isHistoryDrawerOpen ? 'relative' : 'absolute'}
							top={0}
							left={0}
							right={0}
							h="100%"
							minH={0}
							transform={isHistoryDrawerOpen ? 'translateX(0)' : 'translateX(-100%)'}
							opacity={isHistoryDrawerOpen ? 1 : 0}
							visibility={isHistoryDrawerOpen ? 'visible' : 'hidden'}
							transition={compactSlideTransition}
							pointerEvents={isHistoryDrawerOpen ? 'auto' : 'none'}
							{...(!isHistoryDrawerOpen && { inert: '' })}
						>
							<BizySidePanelMenu isMobileMode={isMobileMode} showFullscreenToggle={canShowFullscreenToggle} />
						</Box>
					)}
				</Box>
			</Flex>
			<BizySettingsModalHost />

			{!isFullscreenLayout && !isMobileMode && <BizySidePanelResizeHandle panelLeftEdge={panelLeftEdge} />}
		</Flex>
	);
}
